import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { extractMediaUrlsFromText } from "@/lib/inbox-media";
import { MemberSourceBadge as SharedMemberSourceBadge } from "@/components/member-source-badge";
import { getPadsplitProfileUrl } from "@shared/padsplit-profile-url";
import { Image as ImageIcon, Mic, Video, Paperclip } from "lucide-react";
import type { MemberChat, MessagesPending } from "@shared/schema";
import { substituteMessageVars, type MessageVarContext } from "@/lib/message-placeholders";



// ─── Canned-response shortcut trigger preference ──────────────────────────────
// Per-team-member preference persisted on the app_users row so the shortcut
// follows the user across browsers and devices (Task #2122). The server
// returns NULL when the user has never customized it; we then fall back to
// `/`. An empty string disables the inline autocomplete entirely. Every open
// tab stays in sync through the shared react-query cache.
export const CANNED_TRIGGER_DEFAULT = "/";

export const CANNED_TRIGGER_QUERY_KEY = ["/api/app/profile/canned-response-trigger"] as const;


export type CannedTriggerResponse = { trigger: string | null };


export function useCannedTrigger(): [string, (next: string) => void] {
  const queryClient = useQueryClient();
  const query = useQuery<CannedTriggerResponse>({
    queryKey: CANNED_TRIGGER_QUERY_KEY,
    staleTime: 5 * 60 * 1000,
  });
  const mutation = useMutation({
    mutationFn: async (next: string) => {
      const res = await apiRequest("PUT", "/api/app/profile/canned-response-trigger", { trigger: next });
      return (await res.json()) as CannedTriggerResponse;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<CannedTriggerResponse>(CANNED_TRIGGER_QUERY_KEY, data);
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
    },
  });
  const remote = query.data?.trigger;
  // While loading or when the server has no preference, fall back to "/".
  const trigger = remote === undefined || remote === null ? CANNED_TRIGGER_DEFAULT : remote;
  const update = useCallback((next: string) => {
    // Optimistic local update so the strip + reply box reflect the change
    // immediately while the PUT is in flight.
    queryClient.setQueryData<CannedTriggerResponse>(CANNED_TRIGGER_QUERY_KEY, { trigger: next });
    mutation.mutate(next);
  }, [mutation]);
  return [trigger, update];
}


// Detect whether the cursor in `text` at `cursor` is inside a fresh trigger
// token (the trigger char must be at the very start of the text or directly
// after whitespace, and everything between the trigger and the cursor must be
// non-whitespace). Returns the trigger start index and the query string after
// the trigger, or null when no menu should be open.
export function detectCannedTriggerToken(
  text: string,
  cursor: number,
  trigger: string,
): { start: number; query: string } | null {
  if (!trigger || trigger.length !== 1) return null;
  let start = -1;
  for (let i = cursor - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === trigger) {
      const before = i === 0 ? "" : text[i - 1];
      if (i === 0 || /\s/.test(before)) {
        start = i;
      }
      break;
    }
    if (/\s/.test(ch)) return null;
  }
  if (start === -1) return null;
  return { start, query: text.slice(start + 1, cursor) };
}


export function substituteCannedVars(
  body: string,
  ctx: MessageVarContext,
): string {
  return substituteMessageVars(body, ctx);
}


// ─── Attachment helpers (same caps as modal composer) ────────────────────────
export const ALLOWED_ATTACHMENT_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
  "application/pdf",
]);

export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

export const ATTACHMENT_MAX_COUNT = 10;

// How often the open member conversation refetches its timeline so newly
// arrived messages appear without a manual refresh. Tune here.
export const TIMELINE_POLL_INTERVAL_MS = 10_000;


// sessionStorage key for remembering the last opened conversation on the
// Messages page so navigating away (e.g. via the sidebar) and back restores it.
export const LAST_MEMBER_ID_KEY = "padpulse:messages:lastMemberId";


export type PendingAttachment = {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  status: "uploading" | "ready" | "error";
  objectPath?: string;
  errorMessage?: string;
};


export function makeAttachmentId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}


export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


// ─── Task #1208 — Pending row type extended with rule attribution ─────────────
// Task #1300 follow-up — also includes joined member/property names so the
// Awaiting/Scheduled tabs render "Abram Fisher · Lake House" instead of
// "Member #3".
export type PendingMessageRow = MessagesPending & {
  ruleId: number | null;
  ruleName: string | null;
  memberName: string | null;
  propertyName: string | null;
  isBroadcast: boolean | null;
  broadcastId: string | null;
  // Task #4830 — origin for automations that aren't messaging rules (currently
  // "collections", "low_battery", "business_hours"), which carry no ruleName.
  automationSource?: string | null;
};


// ─── Timeline attachment helpers ──────────────────────────────────────────────
export type WorkflowFields = {
  assigneeId?: number | null;
  assigneeName?: string | null;
  assignedAt?: string | null;
  dueDate?: string | null;
  maintenanceTaskId?: number | null;
  maintenanceLinkType?: string | null;
  // Task #1300 follow-up.
  waitingReplyAt?: string | null;
  isBroadcast?: boolean | null;
  broadcastId?: string | null;
};


export type TimelineChat = MemberChat & {
  attachments?: string[];
  workflow?: WorkflowFields | null;
  replyCount?: number;
  lastReplyAt?: string | null;
  lastReplyAuthorName?: string | null;
  // Task #2989 — Display name of the teammate who resolved this message
  // (resolvedBy → app_users.name), joined server-side. Null when unresolved.
  resolvedByName?: string | null;
  // Pending marker — set by the backend when this row is a not-yet-delivered
  // messages_pending row surfaced into the timeline (so the bubble renders as
  // "Sent" / "Scheduled for X" / "Awaiting approval" with cancel/reschedule
  // actions). PadSplit send-UX — "sent" is the calm optimistic state for a
  // healthy queued row (or one being retried silently); "failed" is TERMINAL
  // only. "sending" is retained for the brief legacy/non-PadSplit path.
  pending?: {
    id: number;
    sendAt: string | null;
    autoSend: boolean;
    status: "sent" | "sending" | "scheduled" | "awaiting" | "failed";
    // Task #1556 — human-readable error from the last failed events-API call.
    sendError?: string | null;
    // Task #1556 — true when the recipient is a PadSplit member.
    isPadsplit?: boolean;
    // Quo messaging — transport carrying this row ('padsplit' | 'quo' |
    // 'twilio'), null when nothing can deliver it. Drives "Sending via Quo…".
    transport?: "padsplit" | "quo" | "twilio" | null;
    // Task #2960 — set when the orphan auto-retry job re-dispatched this
    // message after it sat stuck "published but not sent".
    orphanRetriedAt?: string | null;
    // Messages Auto Draft — AI auto-draft row (Send now / Edit / Cancel).
    autoDraft?: boolean;
  } | null;
  // True when this delivered admin row originated from a host's PadPulse compose
  // → warm-browser send (server derives it from a messages_pending row pointing
  // at this chat via reconciled_chat_id). Drives the blue "LIVE via PadPulse"
  // bubble treatment + the host's own name, distinct from the green "via PS"
  // treatment for messages typed directly inside PadSplit (source='padsplit').
  viaPadPulseLive?: boolean;
  // Task #4266 — automation attribution for admin messages generated by an
  // automatic process (messaging rule or low-battery cron). Null for hand-typed
  // messages. Drives the clickable "Sent by …" chip on the bubble.
  // Task #4712 — "business_hours" marks an after-hours auto-reply (padsplit or
  // portal channel); the bubble shows an "Auto-reply (business hours)" note.
  // Task #4830 — "collections" marks an automated payment-arrangement notice
  // sent by a collections rule; the chip deep-links to that property's
  // collections settings via propertyId.
  // "auto_draft" marks a reply the AI wrote and sent on its own (Messages Auto
  // Draft); drafts the host edited are re-sent manually and stay unmarked.
  automationAttribution?: {
    source: "low_battery" | "rule" | "business_hours" | "collections" | "auto_draft";
    ruleId?: number | null;
    ruleName?: string | null;
    propertyId?: number | null;
  } | null;
  // Optimistic-only fields
  _optimistic?: boolean;
  _optimisticStatus?: "sent" | "sending" | "failed";
};

export function InboxMediaIcon({
  type,
  className,
}: {
  type: "image" | "video" | "audio" | "media";
  className?: string;
}) {
  const Icon =
    type === "video" ? Video : type === "audio" ? Mic : type === "media" ? Paperclip : ImageIcon;
  return <Icon className={className} aria-hidden="true" />;
}


export function attachmentUrlsFor(chat: TimelineChat): string[] {
  if (chat.attachments && chat.attachments.length > 0) return chat.attachments;
  if (chat.senderType !== "admin") return extractMediaUrlsFromText(chat.messageText);
  return [];
}


// ─── Inbox row type ───────────────────────────────────────────────────────────
export type InboxRow = {
  memberId: number;
  memberName: string;
  propertyId: number | null;
  propertyName: string | null;
  lastChatId: number | null;
  lastChatAt: string | null;
  lastChatPreview: string | null;
  lastChatSenderType: string | null;
  unreadCount: number;
  pendingCount: number;
  // PadSplit send-UX — count of terminally-failed outbound rows; drives the red
  // "N failed" conversation-list indicator. 0 when nothing has terminally failed.
  failedCount?: number;
  // Task #2990 — count of open (unresolved) inbound member messages; drives
  // the "N open" sidebar badge. 0 when everything is resolved.
  unresolvedCount: number;
  // Oldest open (unresolved) inbound message timestamp (ISO). Drives the small
  // "open Nh" age hint next to the "N open" badge. Null when nothing is open.
  oldestUnresolvedAt?: string | null;
  // True when this member's effective status bucket is "booking_request".
  isBookingRequest: boolean;
  // Task #1300 follow-up: presence ⇒ "PadSplit" badge, absence ⇒ "Direct".
  padsplitMemberId: string | null;
  // Stay occupant ID is used for the host occupant profile (not the person UID).
  occupantId?: string | null;
};

export { getPadsplitProfileUrl };


// Inbox-level co-presence peer — a teammate currently viewing/typing in a
// conversation, surfaced as a subtle badge on the inbox list row.
export type InboxPresencePeer = {
  actingUserId: number;
  name: string;
  avatarUrl: string | null;
  state: string;
};


export function formatTime(d: string | Date | null) {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d) : d;
  const now = new Date();
  const sameYear = dt.getFullYear() === now.getFullYear();
  const dateStr = dt.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const timeStr = dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${dateStr}, ${timeStr}`;
}


export function formatTimeCompact(d: string | Date | null) {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d) : d;
  const now = new Date();
  const sameDay = dt.toDateString() === now.toDateString();
  if (sameDay) return dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}


// Compact "how long the oldest unresolved message has been open" age, shown
// next to the inbox "N open" badge. Minutes under an hour, then hours, then
// days. Returns "" when there is no timestamp.
export function formatOpenAge(d: string | Date | null): string {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d) : d;
  const ms = Date.now() - dt.getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "<1m";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}


// ─── Member source badge (PadSplit vs Direct) ────────────────────────────────
// Wraps the shared component so Messages, Members list, and Member detail all
// share the same palette (orange for PadSplit, sage green for Direct). See
// `client/src/components/member-source-badge.tsx`.
export function MemberSourceBadge({
  padsplitMemberId,
  memberId,
  compact = false,
  href,
}: {
  padsplitMemberId: string | null | undefined;
  memberId: number | string;
  compact?: boolean;
  /** When provided for a PadSplit member, renders the badge as a clickable link. */
  href?: string | null;
}) {
  return (
    <SharedMemberSourceBadge
      padsplitMemberId={padsplitMemberId}
      memberId={memberId}
      size={compact ? "compact" : "sm"}
      href={href}
    />
  );
}


// ─── Avatar initials helper ───────────────────────────────────────────────────
export function avatarColor(name: string): string {
  const colors = [
    "bg-blue-500", "bg-purple-500", "bg-green-500", "bg-yellow-500",
    "bg-red-500", "bg-pink-500", "bg-indigo-500", "bg-teal-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffff;
  return colors[hash % colors.length];
}


export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
